import { useEffect, useState } from 'react'
import useIntersection from '../useIntersection'

/**
 * Since `next/router.push` is scrolling to the top when redirect to the same page
 * we need 4 switches to trigger the section intersection only once after re-render with the different data
 * while allowing to do the intersection mechanism again
 *
 * 1) prevDeterminer
 *    - the determiner is the item that tell you whether you should reset the intersection or not.
 *      it could be useful when the component isn't unmount but you have to update/reset its content
 *      e.g., PDP's related product section contents has to be reset to the skeleton
 *      upon going to a new product within the same PDP session
 *    - will hold the prev state of the determiner (default: `determinerProps.defaultState`)
 *    - indicate the changes of the determiner, so it will set the `shouldSeeOnce` to `false`
 *      allowing the intersection mechanism to be happen again after the page finished the same-page re-render
 * 2) isVisible
 *    - will be `true` if scroll to the specifed threshold and could be checking for intersectection again with `once` param
 *    - initially `once` = `shouldSeeOnce` (`true`) means that it will checks the section intersection only once
 *    - if `once` is `true`, it won't track the intersection on the current same-page session again
 * 3) isAlreadyViewed
 *    - is true when `isVisible` = `true` indicating that the user is already viewed
 *      the section in the current same-page session
 *    - will be reset to `false` after routing to the next same-page session
 * 4) shouldSeeOnce
 *    - the `useInterSection` will be actively check on the section overlapping back and forth again
 *      based on `once` param if it is `false` or when the user hasn't yet scroll down to the section
 *      or hasn't yet viewed the section recently
 *    - will be set to `false` after determiner has changed and immediately cleanup to its initial state
 *      to reset the `useIntersection` operation
 * 5) Logic flow:
 *    - 1st landing: hasn't view the section yet -> allow to be intersected only for one time
 *    - having determiner data will set the `shouldSeeOnce` to `false` -> reset to `true` after that
 *      it also reset the`useIntersection` operation
 *    - scroll down to the section indicating that it is visible and already viewed -> set `isAlreadyViewed` to `true`
 *      -> won't allowing to perform any custom logic with this `isAllowToPerformIntersectionEvent` variable
 *      (scroll up and down won't do any intersection action)
 *    - 2nd landing: go to the next same-page session -> reset `isAlreadyViewed` to `false`
 *    - repeat
 * @param {object[]} determinerProps           - The variable props that you want the state to change based on it
 * @param {*} determinerProps.defaultState     - The directing variable's default state
 * @param {*} determinerProps.newDeterminer    - The directing variable a.k.a. the determiner
 * @param {object} elemRef                     - The reference of the observing element
 * @param {object[]} intersectionProps         - The object of `useIntersection` custom props
 * @param {number} intersectionProps.threshold - The threshold indicates that how far we'll say that it is intersected
 */
const useResetIntersection = (determinerProps, elemRef, intersectionProps) => {
  const { defaultState, newDeterminer } = determinerProps
  const [prevDeterminer, setPrevDeterminer] = useState(defaultState)
  const [isAlreadyViewed, setIsAlreadyViewed] = useState(false)
  const [shouldSeeOnce, setShouldSeeOnce] = useState(true)

  const isVisible = useIntersection(elemRef, {
    once: shouldSeeOnce,
    ...intersectionProps,
  })

  useEffect(() => {
    if (newDeterminer !== prevDeterminer) {
      setPrevDeterminer(newDeterminer)
      setShouldSeeOnce(false)
    }

    return () => setShouldSeeOnce(true)
  }, [newDeterminer, prevDeterminer])

  useEffect(() => {
    if (isVisible) {
      setIsAlreadyViewed(true)
    }

    return () => setIsAlreadyViewed(false)
  }, [isVisible])

  return {
    isAllowToPerformIntersectionEvent: isVisible && !isAlreadyViewed,
    isAlreadyViewed,
    isVisible,
    shouldSeeOnce,
  }
}

export default useResetIntersection
